import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faSpinner, faCheckCircle, faExclamationTriangle, faBuilding, faUsers, faPhone, faEnvelope, faUser } from '@fortawesome/free-solid-svg-icons';
// Assuming you have initialized Firebase elsewhere and can get the db instance
import { db } from '../firebase-config'; // Adjust this import based on your setup

const ContactSales = () => {
    const { t } = useTranslation("ContactSales"); // Create this namespace

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        companyName: '',
        companySize: '',
        phone: '', // Optional
        interest: '', // Which plan? (Optional)
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
        setError(''); // Clear error on change
        setSuccess(''); // Clear success on change
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Basic Validation
        if (!formData.name || !formData.email || !formData.companyName || !formData.message) {
            setError(t('error.requiredFields'));
            return;
        }
        if (!/\S+@\S+\.\S+/.test(formData.email)) {
            setError(t('error.invalidEmail'));
            return;
        }

        setLoading(true);

        // --- Prepare data for Firestore ---
        const salesRecipient = "keo@keobrand.com"; // The target sales email

        const textBody = `
New Sales Inquiry from ProRecruit Contact Form:

Name: ${formData.name}
Email: ${formData.email}
Company: ${formData.companyName}
Company Size: ${formData.companySize || 'Not Specified'}
Phone: ${formData.phone || 'Not Provided'}
Plan Interest: ${formData.interest || 'Not Specified'}

Message:
--------------------
${formData.message}
--------------------

Timestamp: ${new Date().toLocaleString()}
        `;

        const htmlBody = `
<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
    body { font-family: sans-serif; line-height: 1.6; color: #333; }
    .container { padding: 20px; border: 1px solid #eee; border-radius: 5px; max-width: 600px; }
    h2 { color: #0369a1; /* ProRecruit primary color */ }
    strong { font-weight: bold; }
    p { margin-bottom: 10px; }
    hr { border: 0; border-top: 1px solid #eee; margin: 20px 0; }
</style>
</head>
<body>
<div class="container">
    <h2>New ProRecruit Sales Inquiry</h2>
    <p><strong>Name:</strong> ${formData.name}</p>
    <p><strong>Email:</strong> <a href="mailto:${formData.email}">${formData.email}</a></p>
    <p><strong>Company:</strong> ${formData.companyName}</p>
    <p><strong>Company Size:</strong> ${formData.companySize || 'Not Specified'}</p>
    <p><strong>Phone:</strong> ${formData.phone || 'Not Provided'}</p>
    <p><strong>Plan Interest:</strong> ${formData.interest || 'Not Specified'}</p>
    <hr />
    <p><strong>Message:</strong></p>
    <p>${formData.message.replace(/\n/g, '<br>')}</p> {/* Basic newline handling */}
    <hr />
    <p><small>Submitted via Contact Sales form on ${new Date().toLocaleString()}</small></p>
</div>
</body>
</html>
        `;

        const mailData = {
            to: salesRecipient,
            message: {
                subject: `ProRecruit Sales Inquiry from ${formData.companyName}`,
                text: textBody,
                html: htmlBody,
            },
            categories: [
                "SalesInquiry", // Specific category for this type
                "ContactForm"
            ],
            trackingSettings: {
                clickTracking: {
                    enable: true,
                    enableText: true
                },
                openTracking: {
                    enable: true
                },
                subscriptionTracking: {
                    enable: false // Important for transactional/internal emails
                },
                ganalytics: {
                    enable: true,
                    utmSource: "website", // Source is the website
                    utmMedium: "contact_form",
                    utmCampaign: "ContactSalesForm", // Specific campaign
                    utmTerm: formData.interest || 'general', // Optional: use plan interest as term
                    utmContent: "SalesInquiry"
                }
            },
            customArgs: {
                companyName: formData.companyName, // Add submitted company name
                emailType: "ContactSalesInquiry",
                submittedFrom: "ContactSalesPage"
                // Add other relevant args if needed, e.g., user ID if logged in
            },
            createdAt: serverTimestamp() // Use Firestore server timestamp
            // DO NOT include 'processed' or 'processedTimestamp' here
        };

        // --- Save to Firestore ---
        try {
            const docRef = await addDoc(collection(db, "send-grid-emails"), mailData);
            console.log("Document written with ID: ", docRef.id);
            setSuccess(t('success.messageSent'));
            setFormData({ name: '', email: '', companyName: '', companySize: '', phone: '', interest: '', message: '' }); // Reset form
        } catch (e) {
            console.error("Error adding document: ", e);
            setError(t('error.submitFailed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-gradient-to-b from-sky-50 via-white to-white py-12 md:py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">

                {/* Header Section */}
                <div className="text-center mb-10 md:mb-12">
                    <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 font-bold tracking-tight mb-3">
                        {t('title')}
                    </h1>
                    <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
                        {t('subtitle')}
                    </p>
                </div>

                {/* Optional: Add client logos here */}
                {/* <div className="flex justify-center space-x-8 mb-12 opacity-60 grayscale"> ... logos ... </div> */}

                {/* Form Section */}
                <div className="bg-white p-8 md:p-10 rounded-xl shadow-xl border border-gray-200">
                    <form onSubmit={handleSubmit} className="space-y-6">

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Name */}
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-slate-600 leading-relaxed mb-1">
                                    <FontAwesomeIcon icon={faUser} className="mr-1.5 text-gray-400" /> {t('form.name.label')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    id="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500"
                                    placeholder={t('form.name.placeholder')}
                                />
                            </div>

                            {/* Email */}
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-slate-600 leading-relaxed mb-1">
                                    <FontAwesomeIcon icon={faEnvelope} className="mr-1.5 text-gray-400" /> {t('form.email.label')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    id="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500"
                                    placeholder={t('form.email.placeholder')}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                             {/* Company Name */}
                             <div>
                                <label htmlFor="companyName" className="block text-sm font-medium text-slate-600 leading-relaxed mb-1">
                                   <FontAwesomeIcon icon={faBuilding} className="mr-1.5 text-gray-400" /> {t('form.companyName.label')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="companyName"
                                    id="companyName"
                                    value={formData.companyName}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500"
                                    placeholder={t('form.companyName.placeholder')}
                                />
                            </div>

                            {/* Company Size */}
                             <div>
                                <label htmlFor="companySize" className="block text-sm font-medium text-slate-600 leading-relaxed mb-1">
                                    <FontAwesomeIcon icon={faUsers} className="mr-1.5 text-gray-400" /> {t('form.companySize.label')}
                                </label>
                                <select
                                    name="companySize"
                                    id="companySize"
                                    value={formData.companySize}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500 bg-white"
                                >
                                    <option value="">{t('form.companySize.selectPlaceholder')}</option>
                                    <option value="1-10">{t('form.companySize.option1')}</option>
                                    <option value="11-50">{t('form.companySize.option2')}</option>
                                    <option value="51-200">{t('form.companySize.option3')}</option>
                                    <option value="201-1000">{t('form.companySize.option4')}</option>
                                    <option value="1000+">{t('form.companySize.option5')}</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Phone Number (Optional) */}
                             <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-slate-600 leading-relaxed mb-1">
                                   <FontAwesomeIcon icon={faPhone} className="mr-1.5 text-gray-400" /> {t('form.phone.label')} ({t('form.optional')})
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    id="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500"
                                    placeholder={t('form.phone.placeholder')}
                                />
                            </div>

                             {/* Plan Interest (Optional) */}
                             <div>
                                <label htmlFor="interest" className="block text-sm font-medium text-slate-600 leading-relaxed mb-1">
                                     {t('form.interest.label')} ({t('form.optional')})
                                </label>
                                <select
                                    name="interest"
                                    id="interest"
                                    value={formData.interest}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500 bg-white"
                                >
                                    <option value="">{t('form.interest.selectPlaceholder')}</option>
                                    <option value="Basic">{t('form.interest.optionBasic')}</option>
                                    <option value="Growth">{t('form.interest.optionGrowth')}</option>
                                    <option value="Professional">{t('form.interest.optionProfessional')}</option>
                                    <option value="Premium">{t('form.interest.optionPremium')}</option>
                                    <option value="Enterprise">{t('form.interest.optionEnterprise')}</option>
                                    <option value="Not Sure">{t('form.interest.optionNotSure')}</option>
                                </select>
                            </div>
                        </div>


                        {/* Message */}
                        <div>
                            <label htmlFor="message" className="block text-sm font-medium text-slate-600 leading-relaxed mb-1">
                                {t('form.message.label')} <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                name="message"
                                id="message"
                                rows="5"
                                value={formData.message}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500"
                                placeholder={t('form.message.placeholder')}
                            ></textarea>
                        </div>

                         {/* Feedback Area */}
                        <div className="h-6"> {/* Reserve space for messages */}
                            {error && (
                                <p className="text-sm text-red-600 flex items-center">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1.5" /> {error}
                                </p>
                            )}
                            {success && (
                                <p className="text-sm text-green-600 flex items-center">
                                    <FontAwesomeIcon icon={faCheckCircle} className="mr-1.5" /> {success}
                                </p>
                            )}
                         </div>


                        {/* Submit Button */}
                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className={`w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white ${
                                    loading
                                        ? 'bg-sky-400 cursor-not-allowed'
                                        : 'bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500'
                                } transition duration-150 ease-in-out`}
                            >
                                {loading ? (
                                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                ) : (
                                     <FontAwesomeIcon icon={faPaperPlane} className="mr-2" />
                                )}
                                {loading ? t('form.submitting') : t('form.submitButton')}
                            </button>
                        </div>
                    </form>

                    {/* Optional: Add alternative contact info */}
                    <div className="text-center mt-8 text-sm text-slate-600 leading-relaxed border-t pt-6">
                        <p>{t('alternativeContact.text')} <a href="mailto:sales@prorecruit.tech" className="font-medium text-sky-700 hover:text-sky-600">{t('alternativeContact.email')}</a></p>
                        {/* Add phone number if desired */}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default ContactSales;